import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useTranslation } from 'react-i18next';
import { GeneralSettingInfosManagement } from '../components/GeneralSettingInfosManagement';
import {
  globalEmailSettingSchema,
  TGlobalEmailSettingSchema,
} from '@/schemas/global-email-setting/global-email-setting.schema';
import {
  useUpsertGlobalEmailSetting,
  useGetFirstGlobalEmailSetting,
} from '@/hooks/global-email-setting.hooks';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { showModal } from '@/utils/modal.utils';
import Loader from '@/components/Loading/Loader';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { useGetTab } from '@/hooks/tab';
import { GlobalEmailSettingTab } from '@/type/ui/tab/globalEmailSettingTab';
import { EmailReminderManagement } from '@/components/Common/EmailReminderManagement/Index';
import { GlobalSettingVariableNoteManagement } from '../components/GlobalSettingVariableNoteManagement';

export const GlobalEmailSettingPage = () => {
  const { t } = useTranslation('globalEmailSetting');
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [submittedGlobalEmailSetting, setSubmittedGlobalEmailSetting] =
    useState<TGlobalEmailSettingSchema>();
  const { data, isLoading } = useGetFirstGlobalEmailSetting();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: GlobalEmailSettingTab.GENERAL_SETTINGS,
  });

  const {
    control,
    register,
    setValue,
    watch,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TGlobalEmailSettingSchema>({
    resolver: zodResolver(globalEmailSettingSchema),
  });

  const { mutate, isPending } = useUpsertGlobalEmailSetting({
    t,
    handleSuccess: () => {
      setConfirmUpdate(false);
      setSubmittedGlobalEmailSetting(undefined);
    },
  });

  useEffect(() => {
    if (confirmUpdate && submittedGlobalEmailSetting) {
      mutate(submittedGlobalEmailSetting);
    }
  }, [confirmUpdate, mutate, submittedGlobalEmailSetting]);

  const onSubmitGlobalEmailSetting = (
    globalEmailSetting?: TGlobalEmailSettingSchema,
  ) => {
    const globalEmailSettingWithId = {
      id: data?.data?.id,
      ...globalEmailSetting,
    };

    setSubmittedGlobalEmailSetting(globalEmailSettingWithId);

    showModal('#global-email-setting-confirmation-modal');
  };

  const onSubmitGlobalSettingVariableNote = () => {
    const variableNote = watch('variableNote');
    onSubmitGlobalEmailSetting({ variableNote } as TGlobalEmailSettingSchema);
  };

  return (
    <>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <i className="-mt-1 ki-duotone ki-abstract-46 text-[30px] text-primary" />
            <div>{t('common:emailAutomation')}</div>
          </div>
        }
      />
      <TabList className="mb-5">
        <TabItem
          text={t('generalSettings')}
          leftIcon={<i className="ki-outline ki-setting-4" />}
          active={currentTab === GlobalEmailSettingTab.GENERAL_SETTINGS}
          onChange={() => navigateToTab(GlobalEmailSettingTab.GENERAL_SETTINGS)}
        />
        <TabItem
          text={t('common:automatedEmails')}
          leftIcon={<i className="ki-outline ki-calendar-tick" />}
          active={currentTab === GlobalEmailSettingTab.AUTOMATED_EMAILS}
          onChange={() => navigateToTab(GlobalEmailSettingTab.AUTOMATED_EMAILS)}
        />
        <TabItem
          text={t('variables')}
          leftIcon={<i className="ki-outline ki-notepad-bookmark" />}
          active={currentTab === GlobalEmailSettingTab.VARIABLES}
          onChange={() => navigateToTab(GlobalEmailSettingTab.VARIABLES)}
        />
      </TabList>
      {currentTab === GlobalEmailSettingTab.GENERAL_SETTINGS && (
        <GeneralSettingInfosManagement<TGlobalEmailSettingSchema>
          t={t}
          data={!isLoading && data?.data}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isSubmitting || isPending || isLoading}
          onSubmit={handleSubmit(onSubmitGlobalEmailSetting)}
        />
      )}
      {currentTab === GlobalEmailSettingTab.AUTOMATED_EMAILS && (
        <EmailReminderManagement<TGlobalEmailSettingSchema>
          t={t}
          globalEmailSettingId={data?.data?.id}
          data={!isLoading && data?.data}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isSubmitting || isPending || isLoading}
          onSubmit={handleSubmit(onSubmitGlobalEmailSetting)}
        />
      )}
      {currentTab === GlobalEmailSettingTab.VARIABLES && (
        <GlobalSettingVariableNoteManagement<TGlobalEmailSettingSchema>
          t={t}
          data={!isLoading && data?.data}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isSubmitting || isPending || isLoading}
          onSubmit={onSubmitGlobalSettingVariableNote}
        />
      )}
      <ConfirmationModal
        id="global-email-setting-confirmation-modal"
        title={t('common:confirmation')}
        description={`${t('common:areYouSureToConfirmTheseChanges')} ?`}
        onCancelLabel={t('common:no')}
        onConfirmLabel={t('common:yes')}
        onConfirm={() => setConfirmUpdate(true)}
        onCancel={() => setConfirmUpdate(false)}
      />
      {isLoading && <Loader />}
    </>
  );
};
